import {AuthPayloadResponse, CodeValidationResponse} from "@/schemas/auth.response";
import {ApiEnvelopeBase} from "@/schemas/base.response";
import {ApiClientError, ApiClientErrorPayload, apiRequest} from "@/lib/api-client";

export type AuthErrorPayload = ApiClientErrorPayload;

export class AuthApiError extends Error {
    statusCode: number;
    payload: AuthErrorPayload | null;

    constructor(statusCode: number, payload: AuthErrorPayload | null) {
        super(payload?.message ?? "Auth request failed");
        this.statusCode = statusCode;
        this.payload = payload;
    }
}

export interface LoginInput {
    email: string;
    password: string;
    remember: boolean;
}

export interface RegisterInput {
    email: string;
    password: string;
    displayName: string;
    firstName?: string;
    lastName?: string;
}

export interface UpdateAccountSettingsInput {
    displayName: string;
    firstName?: string | null;
    lastName?: string | null;
    emailUpdates: boolean;
    inAppNotifications: boolean;
}

export async function uploadAvatarRequest(file: File): Promise<AuthPayloadResponse> {
    const formData = new FormData();
    formData.append("file", file);
    return authRequest<AuthPayloadResponse>("/auth/me/avatar", "POST", undefined, formData);
}

export interface ActivateAccountInput {
    code: string;
}

export interface ConfirmPasswordResetInput {
    code: string;
    password: string;
    passwordConfirm: string;
}

async function authRequest<T>(path: string, method: "GET" | "POST" | "PUT", body?: unknown, formData?: FormData): Promise<T> {
    try {
        return await apiRequest<T>({
            path,
            method,
            body,
            formData,
        });
    } catch (e: unknown) {
        if (e instanceof ApiClientError) {
            throw new AuthApiError(e.statusCode, e.payload);
        }
        throw e;
    }
}

export async function loginRequest(payload: LoginInput): Promise<AuthPayloadResponse> {
    return authRequest<AuthPayloadResponse>("/auth/login", "POST", payload);
}

export async function registerRequest(payload: RegisterInput): Promise<AuthPayloadResponse> {
    return authRequest<AuthPayloadResponse>("/auth/register", "POST", payload);
}

export async function meRequest(): Promise<AuthPayloadResponse> {
    return authRequest<AuthPayloadResponse>("/auth/me", "GET");
}

export async function refreshRequest(): Promise<AuthPayloadResponse> {
    return authRequest<AuthPayloadResponse>("/auth/refresh", "POST");
}

export async function updateAccountSettingsRequest(payload: UpdateAccountSettingsInput): Promise<AuthPayloadResponse> {
    return authRequest<AuthPayloadResponse>("/auth/me", "PUT", payload);
}

export async function logoutRequest() {
    return authRequest("/auth/logout", "POST");
}

export async function activateAccountRequest(payload: ActivateAccountInput): Promise<ApiEnvelopeBase> {
    return authRequest<ApiEnvelopeBase>("/auth/activate", "POST", payload);
}

export async function validateActivationCodeRequest(code: string): Promise<CodeValidationResponse> {
    return authRequest<CodeValidationResponse>(`/auth/activate/validate?code=${encodeURIComponent(code)}`, "GET");
}

export async function validatePasswordResetCodeRequest(code: string): Promise<CodeValidationResponse> {
    return authRequest<CodeValidationResponse>("/auth/password-reset/validate", "POST", {code});
}

export async function confirmPasswordResetRequest(payload: ConfirmPasswordResetInput): Promise<ApiEnvelopeBase> {
    return authRequest<ApiEnvelopeBase>("/auth/password-reset/confirm", "POST", payload);
}
